import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { Options } from './shareModes';
import { shareWithSdk, shareWithUrl } from './shareModes';
import { trackShareIntent, trackShareLegacySuccess } from './track';

function tapFacebookShare(options: Options, utilityContext: UtilityContext, track: TrackingFn): void {
	const { trackingEvents, trackingData } = options;

	trackShareIntent(trackingEvents.intent, trackingData, utilityContext, track);

	/**
	 * To avoid impacting existing metrics/dashboards around the `share_petition` event, we'll keep
	 * firing the event after the intent event now that we have a specific tracking event for
	 * successful shares fired in each of the share methods (SDK or URL)
	 *
	 * @see https://change.slack.com/archives/CD1AKHPN2/p1767895632347459
	 */
	trackShareLegacySuccess(trackingEvents.legacy_success, trackingData, utilityContext, track);
}

async function openFacebookShare(
	url: string,
	options: Options,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<boolean> {
	/**
	 * Using sdk in mobile causes a bug with the share dialog (Not correctly redirecting to the app after sharing).
	 * See https://change.slack.com/archives/C0B34DD6K/p1760026406619349 for more details.
	 */
	const { mode = { mobile: 'url', desktop: 'sdk' } } = options;

	const isDesktop = !['mobile', 'tablet'].includes(
		utilityContext.userAgent.getParsedUserAgent().getDevice().type || '',
	);

	const currentMode = isDesktop ? mode.desktop : mode.mobile;

	if (currentMode === 'sdk') {
		return shareWithSdk({ url, options, utilityContext, track, isDesktop });
	}

	return shareWithUrl({ url, options, utilityContext, track, isDesktop });
}

export async function shareFacebook(
	url: string,
	options: Options,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<boolean> {
	tapFacebookShare(options, utilityContext, track);

	return openFacebookShare(url, options, utilityContext, track);
}
